/**
 * Header injection for the auth machine's HTTP calls.
 *
 * The auth actors used to hardcode `clicktap-scope: view` /
 * `clicktap-scope-id: default` — fine for a single-storefront app, fatal for
 * anything else: a wholesale/B2B storefront needs the ACTUAL view id, an
 * `Authorization` header, and a `clicktap-company` header on `/auth/login`,
 * `/auth/refresh` and `/auth/logout`, and those values are only known to the
 * host app (and change over the life of the actor). The quote machines got a
 * `QuoteTransport` input precisely to end this kind of hardcoded coupling —
 * this is the same pattern for auth (CTAP-1159).
 *
 * The provider is a FUNCTION, called immediately before each request, so the
 * headers it returns are current at call time — an `Authorization` built from
 * the token at actor creation would go stale after the first refresh, and
 * `clicktap-company` changes when the visitor picks a company. Its return
 * value is merged OVER the defaults, so a host only overrides what it needs:
 *
 *   createActor(authMachine, {
 *     input: {
 *       transport: {
 *         headers: () => ({
 *           'clicktap-scope-id': viewId,
 *           ...(token ? { Authorization: `Bearer ${token}` } : {}),
 *           ...(company ? { 'clicktap-company': company } : {}),
 *         }),
 *       },
 *       initialContext: { … },
 *     },
 *   });
 *
 * Like `QuoteTransport`, the transport lives in machine context and therefore
 * does not survive snapshot serialization — a consumer restoring a persisted
 * snapshot must recreate the actor with the transport supplied again.
 */

/** Which auth request the headers are being resolved for. */
export type AuthCall = 'login' | 'refresh' | 'ssrRefresh' | 'logout';

/**
 * Returns the headers to merge over {@link DEFAULT_AUTH_HEADERS} for one call.
 * Invoked per request — return fresh values, don't capture stale ones.
 */
export type AuthHeadersProvider = (call: AuthCall) => Record<string, string>;

export type AuthTransport = {
  headers?: AuthHeadersProvider;
};

/**
 * The pre-CTAP-1159 hardcoded values, kept as the default so hosts that never
 * pass a transport behave exactly as before.
 */
export const DEFAULT_AUTH_HEADERS: Record<string, string> = {
  'clicktap-scope': 'view',
  'clicktap-scope-id': 'default',
};

export function resolveAuthHeaders(
  transport: AuthTransport | undefined,
  call: AuthCall,
): Record<string, string> {
  return {
    ...DEFAULT_AUTH_HEADERS,
    ...(transport?.headers?.(call) ?? {}),
  };
}
